<template>
    <div
        class="row-wrapper"
        :class="[checkClass(location, parentLocation), { 'hidden-by-search': isHiddenBySearch() }]"
    >
        <div class="row" :class="{ searched: isISearched() }">
            <div class="location" @click="show = !show">
                <div class="in-location">
                    <button
                        v-if="location.hasChildren()"
                        :class="[show ? 'icon-minus' : 'icon-plus']"
                    ></button>
                    <span :class="{ 'searched-location': isISearchedLocation() }" :title="location.name">{{
                        location.name
                    }}</span>
                </div>
            </div>

            <div class="actions">
                <template v-if="location.getButtonsVisibility()">
                    <!-- Enable/disable calls -->
                    <button
                        v-if="location.hasEnabledOption()"
                        :class="location.getEnabledStatus() ? 'i-active icon-power' : 'i-off icon-off-my'"
                        :title="$t('roomConfiguration.locations.enableDisableCalls')"
                        @click="location.toggleEnabledStatus()"
                    ></button>
                    <!-- Upgrade/downgrade calls -->
                    <span>
                        <i
                            v-if="location.hasUpgradeOption() && location.getUpgradeStatus()"
                            class="icon-mic-plus"
                            :title="$t('roomConfiguration.locations.upgradeDowngradeCalls')"
                            @click="location.toggleUpgradeStatus()"
                        ></i>
                        <button
                            v-if="location.hasUpgradeOption()"
                            class="i-active icon-upgrade-off"
                            :title="$t('roomConfiguration.locations.upgradeDowngradeCalls')"
                            @click="location.toggleUpgradeStatus()"
                        ></button>
                    </span>
                    <!-- Enable/disable listen in -->
                    <button
                        v-if="location.hasListenInOption()"
                        :class="
                            location.getListenInStatus()
                                ? 'i-active icon-listen-in'
                                : 'i-off icon-listen-in-disable'
                        "
                        :title="$t('roomConfiguration.locations.enableDisableListenIn')"
                        @click="location.toggleListenInStatus()"
                    ></button>
                    <!-- Acoustic -->
                    <button
                        v-if="location.hasOwnAcousticOption()"
                        :class="
                            location.getAcousticStatus()
                                ? 'i-active icon-acoustic-settings-on'
                                : 'i-off icon-acoustic-settings-off'
                        "
                        :title="$t('roomConfiguration.locations.acousticCall')"
                        @click.prevent="openModalAcoustic"
                    ></button>
                    <!-- Cancel acoustic calls -->
                    <button
                        v-if="location.isRoot() && location.hasAcousticOption()"
                        class="i-active icon-cancel-calls"
                        :title="$t('roomConfiguration.locations.cancelAcoustic')"
                        @click="cancelAcousticCalls($event)"
                    ></button>

                    <AcousticSettings
                        v-if="location.hasOwnAcousticOption()"
                        :shown="showModalAcoustic"
                        :location="location"
                        @close="showModalAcoustic = false"
                        @ok="setConfig"
                    />
                </template>
            </div>

            <div class="devices">
                <div
                    v-for="(module, index) in location.modules"
                    :key="index"
                    class="device"
                    :class="{ 'searched-device': isSearchedDevice(module) }"
                >
                    <div class="icon-wrapper">
                        <img :src="module.getIcon()" alt="" />
                        <template v-if="module.hasSpeech()">
                            <img v-if="!module.is_nuspm" :src="module.getSpeechIcon()" alt="speech" />
                            <span v-if="validatePhoneNumber(module.phonenumber)">{{ module.phonenumber }}</span>
                        </template>
                    </div>

                    <div class="device-name" :title="module.name">{{ module.name }}</div>
                    <span v-if="module.getButtonsVisibility()">
                        <!-- Enable/disable calls -->
                        <span>
                            <i
                                v-if="module.hasEnabledOption()"
                                :class="[
                                    module.getEnabledStatus() ? 'i-active icon-power' : 'i-off icon-off-my',
                                ]"
                                :title="
                                    module.has_nima
                                        ? $t('roomConfiguration.modules.cannotDisableCallsNIMA')
                                        : module.is_numi
                                        ? $t('roomConfiguration.modules.cannotDisableCallsNUMI')
                                        : $t('roomConfiguration.locations.enableDisableCalls')
                                "
                                @click="module.toggleEnabledStatus()"
                            ></i>
                        </span>
                        <!--upgrade/downgrade-->
                        <span>
                            <i
                                v-if="module.hasUpgradeOption() && module.getUpgradeStatus()"
                                class="icon-mic-plus"
                                :title="$t('roomConfiguration.locations.upgradeDowngradeCalls')"
                                @click="module.toggleUpgradeStatus()"
                            ></i>
                            <button
                                v-if="module.hasUpgradeOption()"
                                class="i-active icon-upgrade-off"
                                :title="$t('roomConfiguration.locations.upgradeDowngradeCalls')"
                                @click="module.toggleUpgradeStatus()"
                            ></button>
                        </span>
                        <!--listen in-->
                        <button
                            v-if="module.hasListenInOption()"
                            :class="
                                module.getListenInStatus()
                                    ? 'i-active icon-listen-in'
                                    : 'i-off icon-listen-in-disable'
                            "
                            :title="$t('roomConfiguration.locations.enableDisableListenIn')"
                            @click="module.toggleListenInStatus()"
                        ></button>
                        <!--acoustic-->
                        <button
                            v-if="module.hasAcousticOption()"
                            :class="module.getAcousticStatus() ? 'i-active icon-mic' : 'i-off icon-no-mic'"
                            :title="$t('roomConfiguration.locations.enableDisableAcoustic')"
                            @click="module.toggleAcousticStatus()"
                        ></button>
                    </span>
                </div>
            </div>
        </div>
        <div v-if="location.children.length > 0" v-show="show">
            <Panel
                v-for="loc in location.children"
                :key="loc.location_id"
                :location="loc"
                :parent-location="location"
                :nothing-found="nothingFound"
                :my-parent-is-searched="isSearched || myParentIsSearched"
                @contentSearched="contentSearchedHandler"
            />
        </div>
    </div>
</template>

<script type="text/babel">
import AcousticSettings from '../room-configuration/AcousticSettings.vue';
import Location from '../room-configuration/Location';
import mappingState from '../common/mappingState';
import { resourceCancelAcousticCalls, resourceRoomConfHistory } from '../../api/resources';
import { SUCCESS, ERROR, COMPONENT_NAMES } from '../../constants';
import { getCancelToken } from '../../api';

export default {
    name: COMPONENT_NAMES.PANEL,

    components: {
        AcousticSettings,
    },

    mixins: [mappingState],

    props: {
        location: {
            type: Location,
            required: true,
        },

        parentLocation: {
            type: Object,
            required: false,
            default: () => ({}),
        },

        myParentIsSearched: {
            type: Boolean,
            default: false,
        },

        nothingFound: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            show: true,
            showModalAcoustic: false,
            isSearched: false,
            isLinkedWithSearch: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        searchedDevices() {
            return this.searchKeyword !== '' && this.location.modules
                ? this.location.modules.filter(
                      module => module.name.toLowerCase().indexOf(this.searchKeyword) !== -1
                  )
                : null;
        },
    },

    watch: {
        searchKeyword: {
            handler(val, oldVal) {
                if (oldVal !== val) {
                    this.isSearched = false;
                    this.isLinkedWithSearch = false;
                }
            },
        },
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        cancelAcousticCalls(e) {
            e.target.blur();

            return this.confirmation({
                confirmTitle: this.$t('roomConfiguration.cancelAcousticCalls'),
                confirmBody: this.$t('roomConfiguration.cancelAcousticCallsConfirm'),
            }).then(r => {
                if (r) {
                    this.showLoading(true);
                    return resourceCancelAcousticCalls({
                        id: this.userAreaId,
                        cancelToken: this.cancelToken.token,
                    })
                        .then(() => {
                            return resourceRoomConfHistory({
                                method: 'post',
                                data: JSON.stringify([
                                    {
                                        location_id: this.location.location_id,
                                        actions: { cancelAcoustic: true },
                                        peripheral_id: null,
                                    },
                                ]),
                                cancelToken: this.cancelToken.token,
                            });
                        })
                        .then(
                            () => {
                                this.showLoading(false);
                                this.popup({
                                    type: SUCCESS,
                                    header: this.$t('common.success'),
                                    text: this.$t('roomConfiguration.cancelMessage'),
                                });
                            },
                            response => {
                                if (response !== 'canceled') {
                                    this.showLoading(false);
                                    if (response.data && response.data.error) {
                                        this.popup({
                                            type: ERROR,
                                            header: this.$t('common.error'),
                                            text: this.$t(response.data.error),
                                        });
                                    }
                                }
                            }
                        );
                }
            });
        },

        openModalAcoustic() {
            this.$nextTick(() => {
                this.showModalAcoustic = !this.showModalAcoustic;
            });
        },

        isSearchedDevice(item) {
            return Array.isArray(this.searchedDevices) && this.searchedDevices.indexOf(item) !== -1;
        },

        isISearchedLocation() {
            return this.searchKeyword !== ''
                ? this.location.name.toLowerCase().indexOf(this.searchKeyword) !== -1
                : false;
        },

        /**
         * Check current location and set css class for it
         * @param location - current location
         * location types: 1 - assignment area, 2-5 - undefined, 6 - room, 7 - bed, 8 - toilet
         * @param parent - parent of current location if it exists
         * @returns class for nested level
         */
        checkClass(location, parent) {
            if (location.type === 1) {
                return '';
            }

            if (location.type === 7 || location.type === 8) {
                let filtered = parent.children.filter(item => item.type === 7 || item.type === 8);
                if (filtered.length === 1) {
                    return 'first-bed last-bed last-location';
                }
                if (filtered.length > 1) {
                    if (filtered[0].name === location.name) {
                        return 'first-bed last-location';
                    }
                    if (filtered[filtered.length - 1].name === location.name) {
                        return 'last-bed last-location';
                    }
                }
                return 'last-location';
            }
            if (parent) {
                if (parent.type === 1 && [2, 3, 4, 5, 6].includes(location.type)) {
                    return 'other-location';
                }
                if (location.type === 6 && [2, 3, 4, 5].includes(parent.type)) {
                    return 'room-location';
                }
                if (parent.type === 6 && [2, 3, 4, 5].includes(location.type)) {
                    return 'room-location';
                }
            }
        },

        isISearched() {
            let filteredModulesCount = Array.isArray(this.searchedDevices) ? this.searchedDevices.length : 0;
            let isSearched =
                this.searchKeyword !== ''
                    ? this.location.name.toLowerCase().indexOf(this.searchKeyword) !== -1 ||
                      filteredModulesCount > 0
                    : false;
            if (this.searchKeyword !== '') {
                this.isSearched = isSearched;
                if (isSearched) {
                    this.$emit('contentSearched');
                }
            }
            return isSearched;
        },

        isHiddenBySearch() {
            return (
                this.searchKeyword !== '' &&
                !this.isSearched &&
                !this.isLinkedWithSearch &&
                !this.myParentIsSearched &&
                !this.nothingFound
            );
        },

        setConfig(settings) {
            this.location.setAcousticStatus(settings.isAcousticEnabled);
            this.location.setActivationTime(settings.activationTime);
            this.location.setSoundLevel(settings.soundLevel);
        },

        contentSearchedHandler() {
            this.$emit('contentSearched', this.location.name);
            this.isLinkedWithSearch = true;
            this.show = true;
        },

        validatePhoneNumber(value) {
            return value !== undefined && value.length > 0;
        },
    },
};
</script>
